import React from "react";
import "../../styles/shared-ui.css";
import "../../styles/modal.css";
import "../../styles/task-detail-modal.css";
import { getCompanyNames } from "../../services/companyService";
import { getJobsByCompanyId } from "../../services/jobService";
import { getTaskWithRelations, updateTask } from "../../services/taskService";
import { getTaskCategories } from "../../services/taskCategoryService";
import { getTaskStatuses } from "../../services/taskStatusService";
import SideTabs from "../layout/SideTabs";
import RichTextEditor from "./RichTextEditor";
import SkillTable from "./SkillTable";
import SkillAddModal from "./SkillAddModal";
import RaciMatrix, { getRaciValidation } from "./RaciMatrix";
import { LEVELS } from "./levels";
import { toast } from "../../utils/toast";

// Same task editor Task Management shows (Basic / Description / Req Skills /
// RACI), packaged as a modal so a diagram can open it in place instead of
// navigating away and losing unsaved workflow edits.

const FREQUENCY_PERIODS = ["DAY", "WEEK", "MONTH", "YEAR"];

const describeRecurrence = (occurrences, frequencyInterval, frequencyPeriod) => {
  const occ = Number(occurrences);
  const interval = Number(frequencyInterval);
  if (
    !Number.isFinite(occ) ||
    !Number.isFinite(interval) ||
    occ <= 0 ||
    interval <= 0
  ) {
    return "";
  }
  const periodLabel = String(frequencyPeriod || "WEEK").toLowerCase();
  const periodText = interval === 1 ? periodLabel : `${periodLabel}s`;
  const timesText = occ === 1 ? "time" : "times";
  return `${occ} ${timesText} in ${interval} ${periodText}`;
};

const EMPTY_FORM = {
  task_name: "",
  task_code: "",
  version: "",
  task_company_id: "",
  expected_process_time: "",
  optimistic_process_time: "",
  pessimistic_process_time: "",
  expected_waiting_time: "",
  expected_rework_time: "",
  status_id: "",
  category_id: "",
  task_overview: "",
  frequency_period: "WEEK",
  frequency_interval: "1",
  occurrences: "1",
  skills: [],
};

/**
 * props:
 * - open, taskId
 * - initialTab — which tab to land on ("basic" | "description" | "skills" |
 *   "raci"); a freshly created task opens on "raci", which is the one thing it
 *   still needs.
 * - onClose()
 * - onSaved(freshTaskWithRelations) — fired after a successful save so the
 *   caller can refresh whatever it renders from the task.
 */
const TaskDetailModal = ({
  open,
  taskId,
  initialTab = "basic",
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [companyName, setCompanyName] = React.useState("");
  const [allJobs, setAllJobs] = React.useState([]);
  const [raciRows, setRaciRows] = React.useState([]);
  const [taskCategories, setTaskCategories] = React.useState([]);
  const [taskStatuses, setTaskStatuses] = React.useState([]);
  const [showAddSkill, setShowAddSkill] = React.useState(false);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  React.useEffect(() => {
    if (!open || taskId == null || taskId === "") return;
    let cancelled = false;

    setData(null);
    setForm(EMPTY_FORM);
    setRaciRows([]);
    setAllJobs([]);
    setCompanyName("");
    setLoading(true);

    (async () => {
      try {
        const [cats, statuses, res] = await Promise.all([
          getTaskCategories().catch(() => []),
          getTaskStatuses().catch(() => []),
          getTaskWithRelations(taskId),
        ]);
        if (cancelled) return;
        setTaskCategories(Array.isArray(cats) ? cats : []);
        setTaskStatuses(Array.isArray(statuses) ? statuses : []);
        setData(res);

        const taskCompanyId = res?.task_company_id ?? res?.company_id ?? "";
        setForm({
          task_name: res?.task_name || "",
          task_code: res?.task_code || "",
          version: res?.task_version || res?.version || "",
          task_company_id: taskCompanyId,
          expected_process_time: res?.expected_process_time ?? "",
          optimistic_process_time: res?.optimistic_process_time ?? "",
          pessimistic_process_time: res?.pessimistic_process_time ?? "",
          expected_waiting_time: res?.expected_waiting_time ?? "",
          expected_rework_time: res?.expected_rework_time ?? "",
          status_id:
            res?.status_id ?? res?.task_status_id ?? res?.task_status?.id ?? "",
          category_id:
            res?.category_id ??
            res?.task_category_id ??
            res?.task_category?.id ??
            "",
          task_overview: res?.task_overview || "",
          frequency_period: res?.frequency_period || "WEEK",
          frequency_interval:
            res?.frequency_interval != null
              ? String(res.frequency_interval)
              : "1",
          occurrences: res?.occurrences != null ? String(res.occurrences) : "1",
          skills: (Array.isArray(res?.taskSkills) ? res.taskSkills : []).map(
            (ts) => ({
              name: ts?.skill?.name || ts?.skill_name || "",
              level: ts?.level?.level_name || "",
            }),
          ),
        });

        setRaciRows(
          (Array.isArray(res?.jobTasks) ? res.jobTasks : [])
            .map((jt) => ({
              job_id: jt?.job_id ?? jt?.job?.job_id,
              role: jt?.role ?? null,
              time_allocation_percentage:
                jt?.time_allocation_percentage != null
                  ? Number(jt.time_allocation_percentage)
                  : null,
            }))
            .filter((r) => r.job_id != null),
        );

        // Jobs already linked to this task can belong to another company, so
        // merge them into the company pool or their RACI rows vanish from the
        // matrix while still counting toward validation.
        const companyJobs = taskCompanyId
          ? await getJobsByCompanyId(taskCompanyId).catch(() => [])
          : [];
        if (cancelled) return;
        const merged = Array.isArray(companyJobs) ? [...companyJobs] : [];
        const seen = new Set(merged.map((j) => String(j?.job_id)));
        for (const jt of Array.isArray(res?.jobTasks) ? res.jobTasks : []) {
          const j = jt?.job;
          if (j && !seen.has(String(j.job_id))) {
            merged.push(j);
            seen.add(String(j.job_id));
          }
        }
        setAllJobs(merged);

        if (res?.company?.name) {
          setCompanyName(res.company.name);
        } else if (taskCompanyId) {
          const companies = await getCompanyNames().catch(() => []);
          if (cancelled) return;
          const match = (Array.isArray(companies) ? companies : []).find(
            (c) => String(c.company_id ?? c.id) === String(taskCompanyId),
          );
          setCompanyName(match?.name || `Company #${taskCompanyId}`);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to load task");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, taskId]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // Let the nested Add Skill dialog take Escape first.
      if (e.key === "Escape" && !showAddSkill) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, showAddSkill]);

  const recurrenceSummary = React.useMemo(
    () =>
      describeRecurrence(
        form.occurrences,
        form.frequency_interval,
        form.frequency_period,
      ),
    [form.occurrences, form.frequency_interval, form.frequency_period],
  );

  // Backend only enforces the RACI rule when `raci` is sent non-empty; mirror
  // that here so the button disables in lockstep with what the save will do.
  const raciValid = raciRows.length === 0 || getRaciValidation(raciRows).valid;

  const taskOverviewText = String(form.task_overview || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  const processTimeValue = Number(form.expected_process_time);
  const processTimeValid =
    form.expected_process_time !== "" &&
    Number.isFinite(processTimeValue) &&
    processTimeValue >= 0;

  const canSave =
    !loading &&
    raciValid &&
    String(form.task_name).trim() &&
    String(form.task_code).trim() &&
    String(form.task_company_id) &&
    taskOverviewText.length >= 10 &&
    processTimeValid;

  const onSubmit = async () => {
    if (!canSave || saving || !data?.task_id) return;
    try {
      setSaving(true);
      await updateTask(data.task_id, {
        task_name: form.task_name,
        task_code: form.task_code,
        version: form.version === "" ? 0 : Number(form.version),
        task_company_id:
          form.task_company_id === "" ? null : Number(form.task_company_id),
        expected_process_time:
          form.expected_process_time === ""
            ? null
            : Number(form.expected_process_time),
        optimistic_process_time:
          form.optimistic_process_time === ""
            ? null
            : Number(form.optimistic_process_time),
        pessimistic_process_time:
          form.pessimistic_process_time === ""
            ? null
            : Number(form.pessimistic_process_time),
        expected_waiting_time:
          form.expected_waiting_time === ""
            ? null
            : Number(form.expected_waiting_time),
        expected_rework_time:
          form.expected_rework_time === ""
            ? null
            : Number(form.expected_rework_time),
        status_id: form.status_id === "" ? null : Number(form.status_id),
        category_id: form.category_id === "" ? null : Number(form.category_id),
        task_overview: form.task_overview,
        frequency_period: form.frequency_period || "WEEK",
        frequency_interval:
          form.frequency_interval === "" ? 1 : Number(form.frequency_interval),
        occurrences: form.occurrences === "" ? 1 : Number(form.occurrences),
        raci: raciRows,
        // Backend expects taskSkills for patch; do not send 'skills' alongside
        taskSkills: (form.skills || [])
          .filter((s) => s.name && s.level)
          .map((s) => ({ skill_name: s.name, level: s.level })),
      });
      toast.success(`Task "${form.task_name}" has been updated successfully!`);
      const fresh = await getTaskWithRelations(data.task_id).catch(() => null);
      onSaved?.(fresh || { ...data, ...form, task_id: data.task_id });
      onClose?.();
    } catch (e) {
      toast.error(e?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div
        className="modal-panel tdm-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Task Details</h3>
            <div className="tdm-subtitle">
              {form.task_name
                ? `Editing: ${form.task_name}`
                : "Update task details, required skills, and RACI assignments"}
              {form.task_code ? ` (${form.task_code})` : ""}
            </div>
          </div>
          <button className="modal-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal-body tdm-body">
          {loading ? (
            <div className="no-results">Loading...</div>
          ) : !data ? (
            <div className="no-results">Task not found</div>
          ) : (
            <SideTabs
              // Remount on task change so `defaultActiveId` is honoured for
              // each task rather than only on first open.
              key={`${taskId}-${initialTab}`}
              defaultActiveId={initialTab}
              tabs={[
                {
                  id: "basic",
                  label: "Basic Details",
                  content: (
                    <div className="role-card">
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Task Name *</label>
                          <input
                            value={form.task_name}
                            onChange={(e) =>
                              setField("task_name", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Task Code *</label>
                          <input
                            value={form.task_code}
                            onChange={(e) =>
                              setField("task_code", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Version</label>
                          <input value={form.version ?? ""} disabled placeholder="0" />
                        </div>
                        <div className="form-group">
                          <label>Company</label>
                          <input value={companyName || "-"} disabled />
                          <small className="hint">
                            Move a task between companies from Task Management —
                            changing it here would detach it from this process.
                          </small>
                        </div>
                        <div className="form-group">
                          <label>Expected Process Time (min) *</label>
                          <input
                            type="number"
                            min={0}
                            value={form.expected_process_time}
                            onChange={(e) =>
                              setField("expected_process_time", e.target.value)
                            }
                          />
                          {!processTimeValid && (
                            <div className="tdm-error">
                              Expected process time is required
                            </div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Optimistic Process Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.optimistic_process_time}
                            onChange={(e) =>
                              setField(
                                "optimistic_process_time",
                                e.target.value,
                              )
                            }
                            placeholder="best case — optional"
                          />
                        </div>
                        <div className="form-group">
                          <label>Pessimistic Process Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.pessimistic_process_time}
                            onChange={(e) =>
                              setField(
                                "pessimistic_process_time",
                                e.target.value,
                              )
                            }
                            placeholder="worst case — optional"
                          />
                          {form.optimistic_process_time !== "" &&
                            form.pessimistic_process_time !== "" &&
                            form.expected_process_time !== "" &&
                            !(
                              Number(form.optimistic_process_time) <=
                                Number(form.expected_process_time) &&
                              Number(form.expected_process_time) <=
                                Number(form.pessimistic_process_time)
                            ) && (
                              <div className="tdm-error">
                                Must satisfy optimistic ≤ expected ≤ pessimistic
                              </div>
                            )}
                        </div>
                        <div className="form-group">
                          <label>Expected Waiting Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.expected_waiting_time}
                            onChange={(e) =>
                              setField("expected_waiting_time", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Expected Rework Time (min)</label>
                          <input
                            type="number"
                            min={0}
                            value={form.expected_rework_time}
                            onChange={(e) =>
                              setField("expected_rework_time", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Task Status</label>
                          <select
                            value={form.status_id}
                            onChange={(e) =>
                              setField("status_id", e.target.value)
                            }
                          >
                            <option value="">Select Status</option>
                            {taskStatuses.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.description || `Status ${s.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Task Category</label>
                          <select
                            value={form.category_id}
                            onChange={(e) =>
                              setField("category_id", e.target.value)
                            }
                          >
                            <option value="">Select Category</option>
                            {taskCategories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.description || `Category ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label title="Total number of times this task should occur within the recurrence window">
                            Occurrences
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={form.occurrences}
                            onChange={(e) =>
                              setField("occurrences", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label title="How many periods to wait between occurrences">
                            Repeat Interval
                          </label>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={form.frequency_interval}
                            onChange={(e) =>
                              setField("frequency_interval", e.target.value)
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label title="Unit of time used for the repeat interval">
                            Period
                          </label>
                          <select
                            value={form.frequency_period}
                            onChange={(e) =>
                              setField("frequency_period", e.target.value)
                            }
                          >
                            {FREQUENCY_PERIODS.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group tdm-full">
                          <label>Recurrence Summary</label>
                          <input value={recurrenceSummary} disabled />
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  id: "description",
                  label: "Description",
                  content: (
                    <div className="role-card">
                      <RichTextEditor
                        label="Task Description *"
                        value={form.task_overview}
                        onChange={(val) => setField("task_overview", val)}
                        height={240}
                      />
                      {taskOverviewText.length < 10 && (
                        <div className="tdm-error">
                          Description must be at least 10 characters
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  id: "skills",
                  label: "Req Skills",
                  content: (
                    <div className="role-card">
                      <div className="tdm-section-head">
                        <span>Req Skills</span>
                        <button
                          type="button"
                          className="secondary-btn sm"
                          onClick={() => setShowAddSkill(true)}
                        >
                          + Add Skill
                        </button>
                      </div>
                      <SkillTable
                        mode="manage"
                        skills={form.skills}
                        levels={LEVELS}
                        onDelete={(idx) =>
                          setForm((prev) => ({
                            ...prev,
                            skills: prev.skills.filter((_, i) => i !== idx),
                          }))
                        }
                      />
                      <SkillAddModal
                        open={showAddSkill}
                        onClose={() => setShowAddSkill(false)}
                        levels={LEVELS}
                        onSave={(s) => {
                          setForm((prev) => ({
                            ...prev,
                            skills: [...prev.skills, s],
                          }));
                          setShowAddSkill(false);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  id: "raci",
                  label: "RACI",
                  content: (
                    <RaciMatrix
                      mode="manage"
                      jobs={allJobs}
                      rows={raciRows}
                      onChange={setRaciRows}
                      label={form.task_name}
                      taskId={data?.task_id}
                    />
                  ),
                },
              ]}
            />
          )}
        </div>

        <div className="modal-footer">
          {!canSave && !loading && data && (
            <span className="tdm-footer-hint">
              {!raciValid
                ? "RACI needs exactly one Accountable and at least one Responsible."
                : "Name, code, a process time, and a 10+ character description are required."}
            </span>
          )}
          <button className="modal-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="primary-btn"
            onClick={onSubmit}
            disabled={!canSave || saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
